import { FC } from 'react';
import { IQueryParamDrivenDrawerContentProvider } from '../../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { QueryParamDrivenDrawerPage } from '../../components/CareMapDrawerPage';
import { IonButton } from '@ionic/react';
import { ActiveCaremapNoteEditor } from './ActiveCaremapNoteEditor';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { useNoteEditor } from './useNoteEditor';

interface IEditNoteDrawerProps {
    noteId: string;
    trackingFrom?: string;
    hasPrivateCheckbox?: boolean;
};

export class EditNoteWithQueryParamDrawer implements IQueryParamDrivenDrawerContentProvider<IEditNoteDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IEditNoteDrawerProps>){}

    mode = 'edit-note';

    Component = EditNoteDrawerView;

    open = (props: IEditNoteDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };

    close = () => {
        this.drawerHelper.close();
    };
};

export const EditNoteDrawerView: FC<IEditNoteDrawerProps> = ({ noteId, hasPrivateCheckbox, trackingFrom }) => {
    const history = useInstance(HistoryViewModel);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    const { noteVM, saveAndClose } = useNoteEditor({
        noteId,
        trackingFrom,
        onSaved: () => drawerHelper.close(),
        navigateToCommunity: () => history.push('/community-redirect')
    });

    if (!noteVM) {
        return null;
    }

    const activeCareMap = careMapState.getActiveCaremap();
    if (!activeCareMap) {
        return null;
    }

    let title = 'Edit Note';

    const canEdit = careMapState.hasPermissionTo('put-note');
    if (!canEdit) {
        title = 'View Note';
    }

    return (
        <QueryParamDrivenDrawerPage
            title={title}
            hasBack={false}
            data-type="note-editor"
            onClose={!canEdit ? drawerHelper.close : undefined}
            buttons={canEdit && (
                <>
                    <IonButton
                        data-type="save-button"
                        disabled={!noteVM.canSave()}
                        onClick={saveAndClose}>Save</IonButton>
                    <IonButton data-type="cancel-button" fill="outline" onClick={() => {
                        history.back();
                    }}>Cancel</IonButton>
                </>
            )}
        >
            <ActiveCaremapNoteEditor
                noteVM={noteVM}
                trackingFrom={trackingFrom}
                onDeleted={drawerHelper.close}
                hasPrivateCheckbox={hasPrivateCheckbox}
                excludeMenu
            />
        </QueryParamDrivenDrawerPage>
    );
};
